import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useQRStore, DotsType, CornersSquareType, CornersDotType, FrameStyle, ErrorCorrectionLevel } from '@/stores/qrStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AISuggestion {
  foreground_color: string;
  background_color: string;
  use_gradient: boolean;
  gradient_color2: string | null;
  gradient_type: 'linear' | 'radial';
  shape_style: string;
  corner_style: string;
  frame_type: string;
  frame_text: string;
  logo_icon: string | null;
  error_correction: string;
  reasoning: string;
}

interface UsageInfo {
  used: number;
  limit: number | null;
  remaining: number | null;
}

// Fallback suggestions when AI fails
const fallbackSuggestions: AISuggestion[] = [
  {
    foreground_color: '#1a1a2e',
    background_color: '#ffffff',
    use_gradient: false,
    gradient_color2: null,
    gradient_type: 'linear',
    shape_style: 'rounded',
    corner_style: 'extra-rounded',
    frame_type: 'simple',
    frame_text: 'SCAN ME',
    logo_icon: 'link',
    error_correction: 'M',
    reasoning: 'Clean, professional design suitable for most business applications.',
  },
  {
    foreground_color: '#228B22',
    background_color: '#F0FFF0',
    use_gradient: true,
    gradient_color2: '#90EE90',
    gradient_type: 'radial',
    shape_style: 'dots',
    corner_style: 'dot',
    frame_type: 'rounded',
    frame_text: '',
    logo_icon: 'heart',
    error_correction: 'M',
    reasoning: 'Eco-friendly, organic feel perfect for sustainable brands.',
  },
  {
    foreground_color: '#0066CC',
    background_color: '#F5F8FC',
    use_gradient: false,
    gradient_color2: null,
    gradient_type: 'linear',
    shape_style: 'square',
    corner_style: 'square',
    frame_type: 'badge',
    frame_text: 'SCAN',
    logo_icon: 'qrcode',
    error_correction: 'H',
    reasoning: 'Corporate, trustworthy design ideal for business cards and professional materials.',
  },
];

interface AISuggestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AISuggestModal({ open, onOpenChange }: AISuggestModalProps) {
  const [useCase, setUseCase] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<AISuggestion | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const { setDesignSettings, setGradientSettings, setFrameSettings, setLogoSettings } = useQRStore();

  const generateSuggestion = async () => {
    if (!useCase.trim()) {
      toast.error('Please enter a use case');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestion(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('ai-design-suggest', {
        body: { useCase: useCase.trim() },
      });

      if (fnError) {
        throw fnError;
      }

      if (data.error) {
        if (data.isPremiumFeature) {
          setError(`Monthly limit reached (${data.used}/${data.limit}). Upgrade to premium for unlimited suggestions.`);
        } else {
          setError(data.error);
        }
        // Use fallback
        const randomFallback = fallbackSuggestions[Math.floor(Math.random() * fallbackSuggestions.length)];
        setSuggestion(randomFallback);
        return;
      }

      setSuggestion(data.suggestion);
      setUsage(data.usage);
    } catch (err: any) {
      console.error('AI suggestion error:', err);
      // Try to get a more specific error message
      const errorMessage = err.message || err.error || 'Failed to get AI suggestion.';
      setError(`${errorMessage} Using preset design instead.`);
      const randomFallback = fallbackSuggestions[Math.floor(Math.random() * fallbackSuggestions.length)];
      setSuggestion(randomFallback);
    } finally {
      setIsLoading(false);
    }
  };

  const applySuggestion = () => {
    if (!suggestion) return;

    // Apply colors and gradient
    setDesignSettings({
      dotsColor: suggestion.foreground_color,
      backgroundColor: suggestion.background_color,
      cornersSquareColor: suggestion.foreground_color,
      cornersDotColor: suggestion.foreground_color,
      dotsType: suggestion.shape_style as DotsType,
      cornersSquareType: suggestion.corner_style as CornersSquareType,
      cornersDotType: (suggestion.corner_style === 'dot' ? 'dot' : 'square') as CornersDotType,
      errorCorrectionLevel: suggestion.error_correction as ErrorCorrectionLevel,
      // Fix: Ensure background transparency is disabled when applying solid/gradient colors
      backgroundTransparent: false,
    });

    // Apply gradient settings
    setGradientSettings({
      enabled: suggestion.use_gradient,
      type: suggestion.gradient_type,
      color1: suggestion.background_color,
      color2: suggestion.gradient_color2 || suggestion.background_color,
    });

    // Apply frame settings
    setFrameSettings({
      style: suggestion.frame_type as FrameStyle,
      text: suggestion.frame_text || 'SCAN ME',
      color: suggestion.foreground_color,
    });

    // Apply logo if suggested and it looks like a URL
    if (suggestion.logo_icon) {
      const isUrl = suggestion.logo_icon.startsWith('http') || suggestion.logo_icon.startsWith('data:');
      setLogoSettings({
        enabled: isUrl,
        url: isUrl ? suggestion.logo_icon : '',
      });
    }

    // Mark as applied in database (fire and forget)
    supabase
      .from('ai_suggestions')
      .update({ applied: true })
      .eq('input', useCase)
      .order('created_at', { ascending: false })
      .limit(1)
      .then(() => {});

    toast.success('Design applied successfully!');
    onOpenChange(false);
  };

  const handleClose = () => {
    setSuggestion(null);
    setError(null);
    setUseCase('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Design Suggestion
          </DialogTitle>
          <DialogDescription>
            Describe your use case and let AI suggest the perfect design for your QR code.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="useCase">What is this QR code for?</Label>
            <Input
              id="useCase"
              placeholder="e.g., coffee shop menu, tech startup, wedding invitation..."
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) {
                  generateSuggestion();
                }
              }}
              disabled={isLoading}
            />
          </div>

          {usage && usage.limit !== null && (
            <p className="text-xs text-muted-foreground">
              {usage.remaining} suggestions remaining this month
            </p>
          )}

          <Button
            onClick={generateSuggestion}
            disabled={isLoading || !useCase.trim()}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Suggestion
              </>
            )}
          </Button>

          {/* Loading skeleton */}
          <AnimatePresence>
            {isLoading && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3"
              >
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                  <div className="h-8 w-full bg-muted animate-pulse rounded" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                  <div className="h-16 w-full bg-muted animate-pulse rounded" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Suggestion preview */}
          <AnimatePresence>
            {suggestion && !isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="space-y-4 p-4 rounded-lg border bg-muted/30"
              >
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Suggested Design</h4>
                  
                  {/* Color preview */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-20">Colors:</span>
                    <div className="flex items-center gap-1">
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: suggestion.foreground_color }}
                        title={suggestion.foreground_color}
                      />
                      <span className="text-xs">on</span>
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ 
                          background: suggestion.use_gradient && suggestion.gradient_color2
                            ? `linear-gradient(135deg, ${suggestion.background_color}, ${suggestion.gradient_color2})`
                            : suggestion.background_color
                        }}
                        title={suggestion.background_color}
                      />
                    </div>
                  </div>

                  {/* Style info */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Shape:</span>{' '}
                      <span className="capitalize">{suggestion.shape_style}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Frame:</span>{' '}
                      <span className="capitalize">{suggestion.frame_type.replace(/-/g, ' ')}</span>
                    </div>
                    {suggestion.logo_icon && (
                      <div>
                        <span className="text-muted-foreground">Logo:</span>{' '}
                        <span className="capitalize">{suggestion.logo_icon}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Error Level:</span>{' '}
                      {suggestion.error_correction}
                    </div>
                  </div>

                  {/* Reasoning */}
                  <p className="text-xs text-muted-foreground italic">
                    "{suggestion.reasoning}"
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateSuggestion}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    <RefreshCw className="mr-2 h-3 w-3" />
                    Regenerate
                  </Button>
                  <Button
                    size="sm"
                    onClick={applySuggestion}
                    className="flex-1"
                  >
                    <Check className="mr-2 h-3 w-3" />
                    Apply Design
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
